import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import { mergeChatAttributes } from './ChatMessageAttributes';
import { isPartialChatMessage, isProgressChatMessage } from './ChatProgress';
import { ChatDeltaResult, ChatMessage, PromptChatAdapter } from './PromptChatAdapter';

export const CHAT_RESPONSE = { queued: 'queued', responding: 'responding', stopping: 'stopping', canceled: 'canceled', completed: 'completed', failed: 'failed', stalled: 'stalled' } as const;
const FALLBACK_READ = { activeMs: 2000, idleMs: 15000, connectedActiveMs: 30000, connectedIdleMs: 60000, maximumBackoffMs: 60000, maximumPages: 10 } as const;

interface UsePromptChatDeltaLoaderOptions {
	adapter: PromptChatAdapter;
	pushConnected?: boolean;
	onComplete?: () => void | Promise<void>;
	onAssistantMessageReceived?: (message: ChatMessage) => void | Promise<void>;
	setErrorBanner: Dispatch<SetStateAction<string | null>>;
	setIsWaiting: Dispatch<SetStateAction<boolean>>;
	setMessages: Dispatch<SetStateAction<ChatMessage[]>>;
}

export default function usePromptChatDeltaLoader({
	adapter,
	pushConnected = false,
	onComplete,
	onAssistantMessageReceived,
	setErrorBanner,
	setIsWaiting,
	setMessages,
}: UsePromptChatDeltaLoaderOptions) {
	const currentSessionIdRef = useRef<string | undefined>(undefined);
	const [currentSessionId, setCurrentSessionIdState] = useState<string | undefined>(undefined);
	const lastMessageIdRef = useRef<string | undefined>(undefined);
	const sequenceRef = useRef<number | undefined>();
	const changeTokenRef = useRef<string | undefined>();
	const lifetimeRef = useRef(new AbortController());
	const [responseStatus, setResponseStatus] = useState<string | undefined>();
	const [responseTurnId, setResponseTurnId] = useState<string | undefined>();
	const responseStatusRef = useRef<string | undefined>();
	const failuresRef = useRef(0);
	const retryAtRef = useRef(0);
	const lastSuccessfulReadAtRef = useRef(0);
	const terminalRef = useRef(false);
	const loadingDeltaRef = useRef(false);
	const queuedDeltaRef = useRef(false);
	const completedSessionIdRef = useRef<string | undefined>(undefined);
	const refreshedAssistantMessageVersionsRef = useRef(new Map<string, string>());

	const stopWaiting = useCallback(() => {
		setIsWaiting(false);
	}, [setIsWaiting]);

	const setCurrentSession = useCallback((sessionId: string | undefined) => {
		if (currentSessionIdRef.current !== sessionId) {
			lifetimeRef.current.abort();
			lifetimeRef.current = new AbortController();
			sequenceRef.current = undefined;
			changeTokenRef.current = undefined;
			lastMessageIdRef.current = undefined;
			responseStatusRef.current = undefined;
			setResponseStatus(undefined);
			setResponseTurnId(undefined);
			terminalRef.current = false;
			failuresRef.current = 0;
			retryAtRef.current = 0;
			lastSuccessfulReadAtRef.current = 0;
		}
		currentSessionIdRef.current = sessionId;
		setCurrentSessionIdState(sessionId);
		if (completedSessionIdRef.current !== sessionId) {
			completedSessionIdRef.current = undefined;
		}
	}, []);

	const setLastMessageFromMessages = useCallback((messages: ChatMessage[], snapshot?: ChatDeltaResult) => {
		lastMessageIdRef.current = messages.length > 0 ? messages[messages.length - 1].id : undefined;
		sequenceRef.current = snapshot?.lastMessageSequence ?? (messages.length ? messages[messages.length - 1].sequence : undefined);
		changeTokenRef.current = snapshot?.changeToken;
		if (snapshot) {
			lastSuccessfulReadAtRef.current = Date.now();
			responseStatusRef.current = snapshot.responseStatus;
			setResponseStatus(snapshot.responseStatus);
			setResponseTurnId(snapshot.responseTurnId);
		}
	}, []);

	const resetSessionTracking = useCallback(() => {
		lifetimeRef.current.abort();
		lifetimeRef.current = new AbortController();
		sequenceRef.current = undefined;
		changeTokenRef.current = undefined;
		responseStatusRef.current = undefined;
		setResponseStatus(undefined);
		setResponseTurnId(undefined);
		currentSessionIdRef.current = undefined;
		setCurrentSessionIdState(undefined);
		lastMessageIdRef.current = undefined;
		completedSessionIdRef.current = undefined;
		queuedDeltaRef.current = false;
		refreshedAssistantMessageVersionsRef.current.clear();
	}, []);

	const notifyCompletedAssistantMessages = useCallback(
		async (messages: ChatMessage[]) => {
			for (const assistantMessage of messages) {
				if (assistantMessage.role !== 'assistant' || isProgressChatMessage(assistantMessage) || isPartialChatMessage(assistantMessage)) {
					continue;
				}

				const version = JSON.stringify([assistantMessage.text, assistantMessage.attributes, assistantMessage.contents]);
				if (refreshedAssistantMessageVersionsRef.current.get(assistantMessage.id) === version) {
					continue;
				}
				refreshedAssistantMessageVersionsRef.current.set(assistantMessage.id, version);
				try {
					await onAssistantMessageReceived?.(assistantMessage);
				} catch (err) {
					console.error('Assistant message callback failed:', err);
				}
			}
		},
		[onAssistantMessageReceived],
	);

	const loadMessagesDelta = useCallback(
		async (sessionId: string) => {
			if (currentSessionIdRef.current !== sessionId) return;
			if (Date.now() < retryAtRef.current) return;
			if (loadingDeltaRef.current) {
				queuedDeltaRef.current = true;
				return;
			}

			loadingDeltaRef.current = true;
			let pages = 0;
			try {
				do {
					queuedDeltaRef.current = false;
					const activeSessionId: string | undefined = currentSessionIdRef.current;
					if (!activeSessionId) {
						return;
					}
					const signal = lifetimeRef.current.signal;

					try {
						const delta: ChatDeltaResult = await adapter.getMessagesDelta(activeSessionId, lastMessageIdRef.current, sequenceRef.current, changeTokenRef.current, signal);
						if (signal.aborted || currentSessionIdRef.current !== activeSessionId) continue;
						failuresRef.current = 0;
						lastSuccessfulReadAtRef.current = Date.now();
						terminalRef.current = false;
						retryAtRef.current = 0;
						sequenceRef.current = delta.lastMessageSequence ?? sequenceRef.current;
						changeTokenRef.current = delta.changeToken;
						responseStatusRef.current = delta.responseStatus;
						setResponseStatus(delta.responseStatus);
						setResponseTurnId(delta.responseTurnId);
						if (delta.responseStatus === CHAT_RESPONSE.queued || delta.responseStatus === CHAT_RESPONSE.responding || delta.responseStatus === CHAT_RESPONSE.stopping) setIsWaiting(true);
						pages++;
						if (delta.hasMoreUpdates && pages < FALLBACK_READ.maximumPages) queuedDeltaRef.current = true;
						if (delta.messages && delta.messages.length > 0) {
							setMessages((prev) => {
								const nextMessages = [...prev];
								const indexById = new Map<string, number>();
								nextMessages.forEach((message, index) => indexById.set(message.id, index));
								for (const message of delta.messages) {
									const existingIndex = indexById.get(message.id);
									if (existingIndex === undefined) {
										indexById.set(message.id, nextMessages.length);
										nextMessages.push(message);
										continue;
									}

									const existingMessage = nextMessages[existingIndex];
									nextMessages[existingIndex] = {
										...existingMessage,
										...message,
										text: message.text ?? existingMessage.text,
										attributes: mergeChatAttributes(existingMessage.attributes, message.attributes),
										attachments: message.attachments ?? existingMessage.attachments,
										contents: message.contents ?? existingMessage.contents,
									};
								}
								return nextMessages;
							});

							lastMessageIdRef.current = delta.messages[delta.messages.length - 1].id;

							await notifyCompletedAssistantMessages(delta.messages);
						}

						if (signal.aborted || currentSessionIdRef.current !== activeSessionId) continue;
						if (delta.responseStatus === CHAT_RESPONSE.failed || delta.responseStatus === CHAT_RESPONSE.stalled || delta.status === 'failed') {
							const failText =
								delta.messages?.find((m) => m.role === 'assistant' && !isProgressChatMessage(m) && !isPartialChatMessage(m))?.text ??
								(delta.responseStatus === CHAT_RESPONSE.stalled
									? 'The agent response has stalled. You can send another message.'
									: 'An error occurred processing your request.');
							setErrorBanner(failText);
							stopWaiting();
						} else if (
							!delta.hasMoreUpdates &&
							delta.isComplete &&
							delta.responseStatus !== CHAT_RESPONSE.queued &&
							delta.responseStatus !== CHAT_RESPONSE.responding &&
							delta.responseStatus !== CHAT_RESPONSE.stopping
						) {
							stopWaiting();
							const completionKey = `${activeSessionId}:${delta.responseTurnId ?? ''}`;
							if (completedSessionIdRef.current !== completionKey) {
								completedSessionIdRef.current = completionKey;
								try {
									await onComplete?.();
								} catch (err) {
									console.error('Chat completion callback failed:', err);
								}
							}
						}
					} catch (err) {
						if (signal.aborted || currentSessionIdRef.current !== activeSessionId) continue;
						failuresRef.current++;
						retryAtRef.current = Date.now() + Math.min(FALLBACK_READ.maximumBackoffMs, FALLBACK_READ.activeMs * 2 ** failuresRef.current);
						console.error('Failed to load chat message updates:', err);
						const status = (err as { status?: number })?.status;
						const headers = (err as { headers?: Record<string, string> })?.headers;
						const retryAfter = Object.entries(headers ?? {}).find(([key]) => key.toLowerCase() === 'retry-after')?.[1];
						if (retryAfter) {
							const seconds = Number(retryAfter);
							const retryAt = Number.isFinite(seconds) && seconds >= 0 ? Date.now() + seconds * 1000 : Date.parse(retryAfter);
							if (Number.isFinite(retryAt)) retryAtRef.current = Math.max(retryAtRef.current, retryAt);
						}
						if (status === 401 || status === 403 || status === 404) {
							terminalRef.current = true;
							setErrorBanner('This chat is no longer accessible. Sign in again or select another session.');
							stopWaiting();
						}
					}
				} while (queuedDeltaRef.current);
			} finally {
				loadingDeltaRef.current = false;
			}
		},
		[adapter, notifyCompletedAssistantMessages, onComplete, setErrorBanner, setIsWaiting, setMessages, stopWaiting],
	);

	useEffect(() => {
		let disposed = false;
		let running = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const available = () => document.visibilityState !== 'hidden' && navigator.onLine !== false;
		const interval = () => {
			const active = responseStatusRef.current === CHAT_RESPONSE.queued || responseStatusRef.current === CHAT_RESPONSE.responding || responseStatusRef.current === CHAT_RESPONSE.stopping;
			return pushConnected ? active ? FALLBACK_READ.connectedActiveMs : FALLBACK_READ.connectedIdleMs
				: active ? FALLBACK_READ.activeMs : FALLBACK_READ.idleMs;
		};
		const poll = async (force = false) => {
			if (disposed || running || !currentSessionId || !available() || terminalRef.current) return;
			const quietRemaining = lastSuccessfulReadAtRef.current + interval() - Date.now();
			if (!force && pushConnected && quietRemaining > 0) {
				timer = setTimeout(poll, quietRemaining);
				return;
			}
			running = true;
			await loadMessagesDelta(currentSessionId);
			running = false;
			if (disposed) return;
			const delay = failuresRef.current
				? Math.min(FALLBACK_READ.maximumBackoffMs, FALLBACK_READ.activeMs * 2 ** failuresRef.current)
				: interval();
			timer = setTimeout(poll, Math.min(2147483647, Math.max(delay, retryAtRef.current - Date.now())));
		};
		const wake = () => {
			clearTimeout(timer);
			if (available()) void poll(true);
		};
		timer = setTimeout(poll, interval());
		document.addEventListener('visibilitychange', wake);
		window.addEventListener('online', wake);
		return () => {
			disposed = true;
			clearTimeout(timer);
			document.removeEventListener('visibilitychange', wake);
			window.removeEventListener('online', wake);
		};
	}, [currentSessionId, loadMessagesDelta, responseStatus, pushConnected]);

	useEffect(() => () => lifetimeRef.current.abort(), []);

	return {
		responseStatus,
		responseTurnId,
		currentSessionId,
		currentSessionIdRef,
		loadMessagesDelta,
		notifyCompletedAssistantMessages,
		resetSessionTracking,
		setCurrentSession,
		setLastMessageFromMessages,
		stopWaiting,
	};
}
